import dayjs, { Dayjs } from 'dayjs';
import { pluralizedUnitOptions } from '@components/shared/UnitSelect/pluralizedUnitOptions';
import { SchedulePeriodUnit } from 'types/EventSeries';

export type { SchedulePeriodUnit };

/**
 * Units a schedule period can be expressed in, in display order. Mirrors
 * PriceUpdates::SchedulePeriod::UNITS. Hand-ordered rather than derived from
 * MAX_SCHEDULE_PERIOD_BY_UNIT's keys, which ESLint forces alphabetical.
 */
export const SCHEDULE_PERIOD_UNITS: readonly SchedulePeriodUnit[] = ['days', 'weeks'];

export const DEFAULT_SCHEDULE_PERIOD_UNIT: SchedulePeriodUnit = 'days';

export const MIN_SCHEDULE_PERIOD = 1;

/** Ceiling per unit, all ~10 years, matching the backend's MAX_DURATION. */
export const MAX_SCHEDULE_PERIOD_BY_UNIT: Record<SchedulePeriodUnit, number> = {
  days: 3650,
  weeks: 520,
};

export const SCHEDULE_PERIOD_UNIT_OPTIONS = (
  t: (key: string, opts?: object) => string,
  count?: number | null,
): { label: string; value: SchedulePeriodUnit }[] =>
  pluralizedUnitOptions(SCHEDULE_PERIOD_UNITS, 'general.unit_', count, t);

const SATURDAY = 6;
const SUNDAY = 0;

const isWeekend = (date: Dayjs) => date.day() === SATURDAY || date.day() === SUNDAY;

const BUSINESS_DAYS_PER_WEEK = 5;

/** Advances `count` weekdays, skipping weekends. */
export const addBusinessDays = (date: Dayjs, count: number): Dayjs => {
  let result = date;
  let remaining = count;

  while (remaining > 0) {
    result = result.add(1, 'day');
    if (!isWeekend(result)) remaining -= 1;
  }

  return result;
};

/** Counts weekdays from `from` (exclusive) through `to` (inclusive). */
export const businessDaysBetween = (from: Dayjs, to: Dayjs): number => {
  let cursor = from.startOf('day');
  const end = to.startOf('day');
  let count = 0;

  while (cursor.isBefore(end)) {
    cursor = cursor.add(1, 'day');
    if (!isWeekend(cursor)) count += 1;
  }

  return count;
};

/** Pushes a weekend date to the following Monday. */
export const nextBusinessDay = (date: Dayjs): Dayjs => {
  if (!isWeekend(date)) return date;

  return date.add(date.day() === SATURDAY ? 2 : 1, 'day');
};

/** This period expressed in weekdays. A week is five business days. */
export const businessDayCount = (value: number, unit: SchedulePeriodUnit): number =>
  (unit === 'weeks' ? value * BUSINESS_DAYS_PER_WEEK : value);

const subtractBusinessDays = (date: Dayjs, count: number): Dayjs => {
  let result = date;
  let remaining = count;

  while (remaining > 0) {
    result = result.subtract(1, 'day');
    if (!isWeekend(result)) remaining -= 1;
  }

  return result;
};

/**
 * Subtracts one period from a date, counted in business days when the event requires weekday
 * round ends, so the result lands on a weekday by construction. A week is five business days.
 */
const subtractPeriod = (
  date: Dayjs,
  value: number,
  unit: SchedulePeriodUnit,
  countBusinessDays: boolean
): Dayjs => {
  if (!countBusinessDays) return date.subtract(value, unit);

  return subtractBusinessDays(date, businessDayCount(value, unit));
};

/** The three boundaries of a price update schedule, derived together. */
export interface DerivedSchedule {
  endTime: Dayjs;
  startTime: Dayjs;
  submissionEnd: Dayjs;
}

export interface ScheduleInputs {
  initialSubmissionUnit: SchedulePeriodUnit;
  initialSubmissionValue: number | null;
  pricesDueBy: Dayjs | null;
  reviewUnit: SchedulePeriodUnit;
  reviewValue: number | null;
  shouldSkipWeekends?: boolean;
}

/**
 * Works backwards from the due date: the review period gives the round boundary, then the
 * submission period gives the start.
 *
 * @returns null when the inputs are incomplete, or the periods leave no positive span for a round
 */
export const deriveSchedule = ({
  initialSubmissionUnit,
  initialSubmissionValue,
  reviewUnit,
  reviewValue,
  pricesDueBy,
  shouldSkipWeekends = false,
}: ScheduleInputs): DerivedSchedule | null => {
  if (!pricesDueBy || initialSubmissionValue == null || reviewValue == null) return null;
  if (initialSubmissionValue < MIN_SCHEDULE_PERIOD || reviewValue < MIN_SCHEDULE_PERIOD) return null;

  const submissionEnd = subtractPeriod(pricesDueBy, reviewValue, reviewUnit, shouldSkipWeekends);
  const startTime = subtractPeriod(submissionEnd, initialSubmissionValue, initialSubmissionUnit, shouldSkipWeekends);

  if (!pricesDueBy.isAfter(submissionEnd) || !submissionEnd.isAfter(startTime)) return null;

  return { endTime: pricesDueBy, startTime, submissionEnd };
};

/** The derived event start, or null when the schedule is incomplete or does not fit. */
export const deriveEventStart = (inputs: ScheduleInputs): Dayjs | null =>
  deriveSchedule(inputs)?.startTime ?? null;

export const isPastDate = (date: Dayjs): boolean => date.isBefore(dayjs().startOf('minute'));
